// used to store ephemeral draft messages that are not saved to the backend
// these are used to show messages that are being generated by the AI assistant
// but have not yet been saved to the backend conversation

import { create } from "zustand";

import { CopilotMessage } from "../api";

export type DraftId = string;

type State = {
  drafts: Record<DraftId, CopilotMessage>;
};

type Actions = {
  addDraft: (draft: CopilotMessage) => void;
  clearDraft: (id: DraftId) => void;
  clearSessionDrafts: (sessionId: string) => void;
  getSessionDrafts: (sessionId: string) => CopilotMessage[];
  updateDraft: (
    id: DraftId,
    patch:
      | ((current: CopilotMessage) => Partial<CopilotMessage>)
      | Partial<CopilotMessage>,
  ) => void;
};

export const useEphemeralDrafts = create<Actions & State>((set, get) => ({
  addDraft: (draft): void =>
    set((s) => ({ drafts: { ...s.drafts, [draft.messageId]: draft } })),

  clearDraft: (id): void =>
    set((s) => {
      const next = { ...s.drafts };
      delete next[id];
      return { drafts: next };
    }),

  clearSessionDrafts: (sessionId): void =>
    set((s) => {
      const next = { ...s.drafts };
      for (const [id, d] of Object.entries(next)) {
        if (d.sessionId === sessionId) delete next[id];
      }
      return { drafts: next };
    }),

  drafts: {},

  getSessionDrafts: (sessionId): CopilotMessage[] => {
    const arr = Object.values(get().drafts);
    return arr.filter((d) => d.sessionId === sessionId);
  },

  updateDraft: (id, patch): void =>
    set((s) => {
      const existing = s.drafts[id];
      if (!existing) return s;
      const patchToApply =
        typeof patch === "function" ? patch(existing) : patch;
      return {
        drafts: { ...s.drafts, [id]: { ...existing, ...patchToApply } },
      };
    }),
}));
